import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import type { StepTestDotStatus } from "../../shared/stepTests/freshness";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import { useStepTestExecutions } from "../hooks/useStepExecutions";
import { useCommitFiles } from "../hooks/useCommitFiles";
import { useStepTestRows } from "../hooks/useStepTestRows";
import { routes, useCurrentUrl } from "../lib/urls";
import {
  ActivityActions as Actions,
  ActivityMenuOpener as KebabOpener,
} from "./ActivityFooter";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { GhostPill } from "./GhostPill";
import { Icon } from "./Icon";
import { shimmer } from "./motion";
import { StatusPill } from "./StatusPill";
import { StepTestNameModal } from "./StepTestNameModal";
import { StepTestStatusDot } from "./StepTestStatusDot";
import { StepTestUnpinConfirmModal } from "./StepTestUnpinConfirmModal";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

const Leading = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const PickerOpener = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  height: 24px;
  padding: 0 8px;
  border-radius: 9999px;
  color: var(--text-primary);
  background: var(--alpha4);

  &:hover {
    background: var(--alpha8);
  }
`;

const OpenerDivider = styled("div")`
  width: 0.5px;
  height: 100%;
  background: var(--border-alpha);
  flex-shrink: 0;
`;

const NameBadge = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  height: 24px;
  padding: 0 8px;
  border-radius: 9999px;
  color: var(--text-primary);
  background: var(--alpha4);
`;

const AUTO_NAME_PLACEHOLDER = /^step-test-\d+$/;

const Shimmering = styled("span")`
  background: linear-gradient(
    90deg,
    var(--text-secondary) 30%,
    var(--text-primary) 50%,
    var(--text-secondary) 70%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  opacity: 0.3;
  animation: ${shimmer} 2000ms linear infinite;
`;

function AnimatedTestName({
  name,
  naming,
}: {
  name: string;
  naming?: boolean;
}) {
  const [display, setDisplay] = useState(name);
  const prevRef = useRef(name);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = name;
    if (naming && AUTO_NAME_PLACEHOLDER.test(name)) {
      // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: blanks the name to drive the typewriter reveal of the resolved auto-name
      setDisplay("");
      return;
    }
    const resolving =
      AUTO_NAME_PLACEHOLDER.test(prev) && !AUTO_NAME_PLACEHOLDER.test(name);
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (!resolving || reduceMotion) {
      setDisplay(name);
      return;
    }
    setDisplay("");
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setDisplay(name.slice(0, i));
      if (i >= name.length) clearInterval(id);
    }, 32);
    return () => clearInterval(id);
  }, [name, naming]);

  if (naming && AUTO_NAME_PLACEHOLDER.test(name)) {
    return <Shimmering>Naming test…</Shimmering>;
  }
  return display;
}

type Props = {
  workflowId: string;
  canWrite: boolean;
  branchId: string;
  commitId: string;
  stepDir: string;
  testNames: readonly string[];
  activeTestName: string | null;
  naming?: boolean;
  capturing: boolean;
  onInvestigateFailure: (params: { stepDir: string; testName: string }) => void;
};

export function StepTestFooter({
  canWrite,
  workflowId,
  branchId,
  commitId,
  stepDir,
  testNames,
  activeTestName,
  naming,
  capturing,
  onInvestigateFailure,
}: Props) {
  const z = useZero();
  const { stepTest: urlStepTest } = useCurrentUrl(routes.space.workflow);
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId);
  const [commitFiles, commitFilesResult] = useCommitFiles(commitId);
  const { executions, loading: executionsLoading } = useStepTestExecutions(
    workflowId,
    branchId,
    stepDir
  );
  const { rows, ready } = useStepTestRows({
    commitFiles: commitFiles ?? [],
    stepDir,
    testNames,
    executions,
  });
  const rowByName = new Map(rows.map((r) => [r.name, r]));

  const [picked, setPicked] = useState<string | null>(null);
  const [unpinName, setUnpinName] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  if (!canWrite) {
    if (unpinName !== null) setUnpinName(null);
    if (renaming) setRenaming(false);
  }
  const selectedTest =
    picked && testNames.includes(picked)
      ? picked
      : urlStepTest && testNames.includes(urlStepTest)
        ? urlStepTest
        : activeTestName && testNames.includes(activeTestName)
          ? activeTestName
          : (testNames[0] ?? null);
  const selectedRow = selectedTest
    ? (rowByName.get(selectedTest) ?? null)
    : null;

  const status = capturing ? "running" : (selectedRow?.status ?? "notrun");
  const running = status === "running";

  // Keep the last settled result in the pill while a re-check is in flight (the
  // button spinner conveys the run), so the status doesn't blank out or flash.
  // Keyed by test so switching the picker shows the right test's last result.
  const settledStatus = running ? null : status;
  const [lastSettled, setLastSettled] = useState<
    ReadonlyMap<string, StepTestDotStatus>
  >(new Map());
  if (
    settledStatus &&
    selectedTest &&
    lastSettled.get(selectedTest) !== settledStatus
  ) {
    const next = new Map(lastSettled);
    next.set(selectedTest, settledStatus);
    setLastSettled(next);
  }

  // Until the commit files, executions, and freshness hashes have all resolved,
  // the status would default to "not checked" — so show a placeholder rather
  // than flashing a wrong status while a step with a known result loads in.
  const loading =
    !capturing &&
    (commitFilesResult?.type !== "complete" || executionsLoading || !ready);
  if (loading) {
    return (
      <>
        <Leading />
        <Actions>
          <GhostPill style={{ width: 64, height: 20 }} />
        </Actions>
      </>
    );
  }

  const pillStatus: StepTestDotStatus | null = capturing
    ? "running"
    : running
      ? selectedTest
        ? (lastSettled.get(selectedTest) ?? null)
        : null
      : status;

  const label = capturing
    ? "Capturing storage fixtures…"
    : running && (pillStatus === null || pillStatus === "notrun")
      ? "Running"
      : pillStatus === "passed"
        ? "Passing"
        : pillStatus === "failed"
          ? "Failing"
          : pillStatus === "skipped"
            ? "Skipped"
            : "Not checked";

  const hint =
    !running && !capturing && selectedRow?.status === "failed"
      ? (selectedRow.message ?? "This run didn’t reproduce")
      : capturing
        ? "Capturing storage fixtures…"
        : running
          ? testNames.length > 1
            ? "Running tests…"
            : "Running test…"
          : pillStatus === "passed"
            ? "Test passed"
            : pillStatus === "skipped"
              ? "Test skipped"
              : "Run tests to check this step";
  // The runner reports a mismatch as `expected [x] but got [y]`; split it so the
  // tooltip shows expected and result on their own lines. Other messages (e.g.
  // "step exited with code 1") render as-is.
  const mismatch = hint?.match(
    /^expected \[([\s\S]*)\] but got \[([\s\S]*)\]$/
  );
  const hintContent = mismatch ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        color: "var(--tooltip-fg)",
      }}
    >
      <div>
        <Text variant="text-small-md">Expected</Text>{" "}
        <Text variant="text-small" color="var(--tooltip-fg-subtle)">
          {mismatch[1] ?? ""}
        </Text>
      </div>
      <div>
        <Text variant="text-small-md">Result</Text>{" "}
        <Text variant="text-small" color="var(--tooltip-fg-subtle)">
          {mismatch[2] ?? ""}
        </Text>
      </div>
    </div>
  ) : (
    <Text variant="text-small-md" color="var(--tooltip-fg)">
      {hint}
    </Text>
  );

  const runTests = () =>
    canWrite &&
    z.mutate(
      sharedMutators.runStepTests({
        runId: nanoid(12),
        workflowId,
        commitId,
        stepDir,
        executionId: nanoid(12),
      })
    );

  const removeTest = (name: string) =>
    canWrite &&
    navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.unpinStepTest({
          workflowId,
          ...branchParams,
          stepDir,
          name,
        })
      )
    );

  const renameTest = (newName: string) => {
    if (!canWrite || !selectedTest) return;
    void navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.renameStepTest({
          workflowId,
          ...branchParams,
          stepDir,
          name: selectedTest,
          newName,
        })
      )
    );
  };

  return (
    <>
      <Leading>
        {selectedTest && testNames.length > 1 && (
          <Dropdown
            align="top-left"
            opener={
              <PickerOpener>
                <Text
                  variant="text-small-md"
                  color="var(--text-primary)"
                  style={{ padding: "0 2px" }}
                >
                  {testNames.length}
                </Text>
                <OpenerDivider />
                <Text variant="text-small-md" ellipsis>
                  <AnimatedTestName name={selectedTest ?? ""} naming={naming} />
                </Text>
                <Icon name="chevron-down" size={12} />
              </PickerOpener>
            }
            sections={[
              {
                items: testNames.map((name) => ({
                  type: "button" as const,
                  key: name,
                  left: <Icon name="microscope" size={16} />,
                  label: name,
                  right: (
                    <StepTestStatusDot
                      status={rowByName.get(name)?.status ?? "notrun"}
                    />
                  ),
                  selected: name === selectedTest,
                  onClick: () => setPicked(name),
                })),
              },
            ]}
          />
        )}
        {selectedTest && testNames.length === 1 && (
          <NameBadge>
            <Icon name="microscope" size={12} />
            <Text variant="text-small-md" ellipsis>
              <AnimatedTestName name={selectedTest} naming={naming} />
            </Text>
          </NameBadge>
        )}
      </Leading>
      {selectedTest && (
        <Actions>
          <StatusPill
            color={
              pillStatus === "passed"
                ? "green"
                : pillStatus === "failed"
                  ? "red"
                  : "gray"
            }
            label={label}
            detail={hintContent}
            running={running}
            action={
              capturing
                ? undefined
                : {
                    label: testNames.length > 1 ? "Run tests" : "Run test",
                    onClick: runTests,
                    disabled: !canWrite,
                  }
            }
            triggerLabel={
              running && !capturing && label !== "Running"
                ? `${label}, running tests`
                : label
            }
          />
          {!capturing && (
            <>
              {selectedRow?.status === "failed" && (
                <Tooltip content="Fix test failure">
                  <Button
                    variant="secondary"
                    size="m"
                    icon="radar"
                    aria-label="Fix test failure"
                    disabled={!canWrite}
                    onClick={() =>
                      onInvestigateFailure({ stepDir, testName: selectedTest })
                    }
                  />
                </Tooltip>
              )}
              <Dropdown
                align="top"
                minWidth={160}
                maxWidth={160}
                opener={
                  <KebabOpener aria-label="More">
                    <Icon name="more" size={16} />
                  </KebabOpener>
                }
                sections={[
                  {
                    items: [
                      {
                        type: "button" as const,
                        key: "rename",
                        left: <Icon name="pencil" size={16} />,
                        label: "Rename",
                        disabled: !canWrite,
                        onClick: () => setRenaming(true),
                      },
                      {
                        type: "button" as const,
                        key: "delete",
                        left: <Icon name="trash" size={16} />,
                        label: "Delete",
                        disabled: !canWrite,
                        onClick: () => setUnpinName(selectedTest),
                      },
                    ],
                  },
                ]}
              />
            </>
          )}
        </Actions>
      )}
      <StepTestUnpinConfirmModal
        name={canWrite ? unpinName : null}
        onClose={() => setUnpinName(null)}
        onConfirm={(name) => void removeTest(name)}
      />
      <StepTestNameModal
        open={renaming && canWrite}
        title="Rename step test"
        confirmLabel="Rename"
        initialName={selectedTest ?? ""}
        takenNames={testNames.filter((n) => n !== selectedTest)}
        onClose={() => setRenaming(false)}
        onSubmit={renameTest}
      />
    </>
  );
}
